/* eslint-disable no-unused-vars */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import validateEmail from "../utils/validateEmail";
import instance, {
  fetchAssessmentDueDate,
  fetchEntryDueDate,
  fetchInitialResponseDueDate,
  fetchRatingDueDate,
  fetchResponseRatingDueDate,
} from "../api/api";
import { jwtDecode } from "jwt-decode";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { Loader } from "./Loader";
import { usePostHog } from "@posthog/react";
import { useTenant } from "../context/Tenant/TenantContext";
import { getCurrentDomain } from "../utils/configLoader";
import { getBrandTheme } from "../utils/tenantTheme";

// ── Theme tokens (same keys as Admin/EmployeeWrapper) ──────────────────────────
const THEMES = {
  blueprint: { lp: "#1c3562", pri: "#1d4ed8" },
  obsidian:  { lp: "#0c1220", pri: "#3b82f6" },
  daylight:  { lp: "#1e40af", pri: "#0284c7" },
};

const INPUT_STYLE = {
  width: "100%",
  padding: "11px 14px",
  border: "1.5px solid #e5e7eb",
  borderRadius: 9,
  fontSize: 14,
  color: "#0f172a",
  background: "#fafafa",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const LABEL_STYLE = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
  marginBottom: 6,
};

const PASSWORD_TOGGLE_STYLE = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "#94a3b8",
  display: "flex",
  alignItems: "center",
};

const FEATURE_ITEMS = [
  "Risk Registration & Assessment",
  "Response Planning & Monitoring",
  "Role-based Workflows & Approval",
];

export default function Login() {
  const { tenant, loading: tenantLoading } = useTenant();
  const navigate = useNavigate();
  const posthog = usePostHog();

  // Theme
  const [theme, setTheme] = useState(
    () => localStorage.getItem("riskTheme") || "daylight"
  );
  const t = getBrandTheme(tenant?.branding, THEMES[theme] || THEMES.daylight);

  const applyTheme = (name) => {
    setTheme(name);
    localStorage.setItem("riskTheme", name);
  };

  // Navigation paths
  const [adminPath,    setAdminPath]    = useState("/admin/adminriskregistry");
  const [employeePath, setEmployeePath] = useState("/employee/riskregistry");

  useEffect(() => {
    const fetchDueDates = async () => {
      await fetchEntryDueDate();
      await fetchRatingDueDate();
      await fetchAssessmentDueDate();
      await fetchInitialResponseDueDate();
      await fetchResponseRatingDueDate();
      setAdminPath("/admin/adminriskregistry");
      setEmployeePath("/employee/riskregistry");
    };
    fetchDueDates();
  }, []);

  // Departments
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    instance.get("/api/departments/all")
      .then(r => setDepartments(r.data.data ?? []))
      .catch(() => {});
  }, []);

  // Active tab
  const [activeTab, setActiveTab] = useState("login");

  // ── Login state ──
  const [loginFormValue, setLoginFormValue] = useState({ loginEmail: "", loginPassword: "" });
  const [loginFormErrors, setLoginFormErrors] = useState({ loginEmail: "", loginPassword: "" });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [pageError,   setPageError]   = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginFormValue(prev => ({ ...prev, [name]: value }));
  };

  const validateLogin = (value) => {
    const errors = { loginEmail: "", loginPassword: "" };
    const textRegex = /^[a-z]/i;
    if (!value.loginEmail)    errors.loginEmail    = "Email address Required";
    if (!value.loginPassword) errors.loginPassword = "Password is required";
    if ((value.loginEmail.includes("@") || textRegex.test(value.loginEmail)) &&
        !validateEmail().test(value.loginEmail)) {
      errors.loginEmail = "Invalid email format";
    }
    return errors;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginFormErrors(validateLogin(loginFormValue));
    setIsLoggingIn(true);
    try {
      const response = await instance.post("/api/auth/login", {
        email: loginFormValue.loginEmail,
        password: loginFormValue.loginPassword,
      });
      const token = response.data.accessToken;
      localStorage.setItem("token", token);
      if (token) {
        const decoded = jwtDecode(token);
        const { userId, role } = decoded;
        localStorage.setItem("name", decoded.firstName + " " + decoded.lastName);
        localStorage.setItem("email", decoded.email);
        if (role !== "ADMIN" && decoded.department) {
          localStorage.setItem("department", decoded.department.name);
        }
        localStorage.setItem("userId", userId);
        localStorage.setItem("role", role);
        switch (role) {
          case "EMPLOYEE": {
            posthog.identify(userId, { email: decoded.email, role, department: decoded.department?.name, tenant: getCurrentDomain() });
            posthog.capture("user_logged_in", { role, tenant: getCurrentDomain() });
            navigate(employeePath);
            window.location.reload();
            break;
          }
          case "ADMIN": {
            posthog.identify(userId, { email: decoded.email, role, tenant: getCurrentDomain() });
            posthog.capture("user_logged_in", { role, tenant: getCurrentDomain() });
            navigate(adminPath);
            window.location.reload();
            break;
          }
          default:
            posthog.identify(userId, { email: decoded.email, role, tenant: getCurrentDomain() });
            posthog.capture("user_logged_in", { role, tenant: getCurrentDomain() });
            navigate("/employee/riskregistry");
        }
      }
    } catch (error) {
      posthog.capture("login_failed", { error: error.response?.data?.message });
      setPageError(error.response?.data?.message ?? "Login failed. Please try again.");
      setIsLoggingIn(false);
    }
  };

  // ── Signup state ──
  const signupInitialValue = useMemo(() => ({
    firstName: "", lastName: "", signupEmail: "",
    signupPassword: "", confirmPassword: "", department: "",
  }), []);

  const [signupFormValue,  setSignupFormValue]  = useState(signupInitialValue);
  const [signupFormErrors, setSignupFormErrors] = useState({});
  const [isSignupLoading,  setIsSignupLoading]  = useState(false);
  const [signupSuccess,    setSignupSuccess]    = useState("");
  const [signupError,      setSignupError]      = useState("");
  const [showSignupPassword,  setShowSignupPassword]  = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupFormValue(prev => ({ ...prev, [name]: value }));
  };

  const validateSignup = (value) => {
    const errors = {};
    const lc = /[a-z]/, uc = /[A-Z]/, sp = /[!@#$%^&*(),.?":{}|<>]/, num = /[0-9]/;
    if (!value.firstName)  errors.firstName  = "First name is required";
    if (!value.lastName)   errors.lastName   = "Last name is required";
    if (!value.department) errors.department = "Department is required";
    if (!value.signupEmail) errors.signupEmail = "Email address Required";
    if (!value.signupPassword) {
      errors.signupPassword = "Password is required";
    } else if (value.signupPassword.length < 8) {
      errors.signupPassword = "Password must be at least 8 characters long";
    } else if (!lc.test(value.signupPassword)) {
      errors.signupPassword = "Password must contain at least one lowercase letter";
    } else if (!uc.test(value.signupPassword)) {
      errors.signupPassword = "Password must contain at least one uppercase letter";
    } else if (!sp.test(value.signupPassword)) {
      errors.signupPassword = "Password must contain at least one special character";
    } else if (!num.test(value.signupPassword)) {
      errors.signupPassword = "Password must contain at least one number";
    }
    if (!value.confirmPassword) {
      errors.confirmPassword = "Confirm Password is required";
    } else if (value.signupPassword !== value.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    return errors;
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    const errors = validateSignup(signupFormValue);
    setSignupFormErrors(errors);
    if (Object.values(errors).some(Boolean)) return;
    setIsSignupLoading(true);
    setSignupError("");
    instance.post("/api/auth/signup", {
      email: signupFormValue.signupEmail,
      password: signupFormValue.signupPassword,
      firstName: signupFormValue.firstName,
      departmentId: signupFormValue.department,
      lastName: signupFormValue.lastName,
      confirmPassword: signupFormValue.confirmPassword,
    })
      .then(response => {
        setSignupSuccess(response.data.message || "Account created! Check your email.");
        posthog.capture("user_signed_up");
        setIsSignupLoading(false);
        setSignupFormValue(signupInitialValue);
      })
      .catch(error => {
        setSignupError(error.response?.data?.message ?? "Sign up failed. Please try again.");
        setIsSignupLoading(false);
      });
  };

  if (tenantLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader size="xl" />
      </div>
    );
  }

  const companyName = tenant?.companyName || "RSM Botswana";

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* ── Left brand panel ── */}
      <div style={{
        width: "42%", flexShrink: 0,
        background: t.lp,
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        padding: "48px 44px", position: "relative", overflow: "hidden",
      }}>
        {/* Decorative rings */}
        <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.06)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: -50, right: -50, width: 250, height: 250, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.09)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: 80, left: -80, width: 320, height: 320, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.05)", pointerEvents: "none" }} />

        {/* Top content */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 52 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden",
            }}>
              <img src={tenant?.branding?.logoUrl || "/rsmLogo.png"} alt="logo" style={{ width: 30, height: 30, objectFit: "contain" }} />
            </div>
            <span style={{ color: "#fff", fontSize: 17, fontWeight: 700, letterSpacing: "-0.3px" }}>
              {companyName}
            </span>
          </div>

          <h1 style={{ color: "#fff", fontSize: 30, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", margin: "0 0 14px 0", maxWidth: 280 }}>
            Enterprise Risk Management Platform
          </h1>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, lineHeight: 1.7, maxWidth: 280, margin: "0 0 36px 0" }}>
            Identify, assess, and manage organizational risks with confidence across every department.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {FEATURE_ITEMS.map(item => (
              <div key={item} style={{ display: "flex", alignItems: "center", gap: 10, color: "rgba(255,255,255,0.8)", fontSize: 13 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, fontWeight: 800, flexShrink: 0,
                }}>
                  ✓
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, margin: 0 }}>
            Secure • Role-based • Multi-tenant
          </p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={{
        flex: 1, background: "#fff",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 48, position: "relative", overflowY: "auto",
      }}>

        {/* Theme picker */}
        <div style={{ position: "absolute", top: 24, right: 24, display: "flex", alignItems: "center", gap: 4, background: "#f1f5f9", borderRadius: 10, padding: 4 }}>
          {Object.keys(THEMES).map(key => {
            const active = theme === key;
            return (
              <button
                key={key}
                onClick={() => applyTheme(key)}
                style={{
                  padding: "5px 12px", borderRadius: 7, border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 700, transition: "all 0.15s", fontFamily: "inherit",
                  background: active ? "#fff" : "transparent",
                  color: active ? "#0f172a" : "#94a3b8",
                  boxShadow: active ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                }}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            );
          })}
        </div>

        <div style={{ width: "100%", maxWidth: 360 }}>
          {/* Tabs */}
          <div style={{ display: "flex", marginBottom: 32, borderBottom: "2px solid #f1f5f9" }}>
            {["login", "signup"].map(tab => {
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1, padding: "12px 0", background: "none", border: "none",
                    borderBottom: `2px solid ${active ? t.pri : "transparent"}`,
                    marginBottom: -2,
                    color: active ? t.pri : "#94a3b8",
                    fontSize: 15, fontWeight: active ? 700 : 500,
                    cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit",
                  }}
                >
                  {tab === "login" ? "Login" : "Sign Up"}
                </button>
              );
            })}
          </div>

          {/* ── Login form ── */}
          {activeTab === "login" && (
            <div style={{ animation: "fadeUp 0.25s ease" }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px", margin: "0 0 6px 0" }}>
                Welcome back
              </h2>
              <p style={{ color: "#94a3b8", fontSize: 14, margin: "0 0 28px 0" }}>
                Sign in to your {companyName} account
              </p>

              <form onSubmit={handleLoginSubmit} noValidate>
                <div style={{ marginBottom: 16 }}>
                  <label style={LABEL_STYLE} htmlFor="loginEmail">Email address</label>
                  <input
                    type="email"
                    id="loginEmail"
                    name="loginEmail"
                    placeholder="name@email.com"
                    style={INPUT_STYLE}
                    value={loginFormValue.loginEmail}
                    onChange={handleLoginChange}
                    required
                  />
                  {loginFormErrors.loginEmail && (
                    <p style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{loginFormErrors.loginEmail}</p>
                  )}
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label style={LABEL_STYLE} htmlFor="loginPassword">Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      id="loginPassword"
                      name="loginPassword"
                      placeholder="••••••••"
                      style={{ ...INPUT_STYLE, paddingRight: 40 }}
                      value={loginFormValue.loginPassword}
                      onChange={handleLoginChange}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(v => !v)}
                      style={PASSWORD_TOGGLE_STYLE}
                      aria-label={showLoginPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showLoginPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
                    </button>
                  </div>
                  {loginFormErrors.loginPassword && (
                    <p style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{loginFormErrors.loginPassword}</p>
                  )}
                  {pageError && (
                    <p style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{pageError}</p>
                  )}
                </div>

                <div style={{ textAlign: "right", marginBottom: 24 }}>
                  <a href="/forgot-password" style={{ fontSize: 13, color: t.pri, fontWeight: 500, textDecoration: "none" }}>
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={isLoggingIn}
                  style={{
                    width: "100%", padding: 13, background: t.pri, color: "#fff",
                    border: "none", borderRadius: 9, fontSize: 15, fontWeight: 700,
                    cursor: isLoggingIn ? "not-allowed" : "pointer", letterSpacing: "-0.2px",
                    fontFamily: "inherit", opacity: isLoggingIn ? 0.8 : 1, transition: "opacity 0.15s",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {isLoggingIn ? <Loader size="sm" /> : "Sign In →"}
                </button>
              </form>
            </div>
          )}

          {/* ── Signup form ── */}
          {activeTab === "signup" && (
            <div style={{ animation: "fadeUp 0.25s ease" }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px", margin: "0 0 6px 0" }}>
                Create account
              </h2>
              <p style={{ color: "#94a3b8", fontSize: 14, margin: "0 0 24px 0" }}>
                Access credentials will be emailed to you after sign up
              </p>

              {signupSuccess && (
                <div style={{ padding: "12px 16px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 9, marginBottom: 20, fontSize: 13, color: "#16a34a", fontWeight: 500 }}>
                  {signupSuccess}
                </div>
              )}
              {signupError && (
                <div style={{ padding: "12px 16px", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 9, marginBottom: 20, fontSize: 13, color: "#dc2626", fontWeight: 500 }}>
                  {signupError}
                </div>
              )}

              <form onSubmit={handleSignUp} noValidate>
                {/* First / Last name */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={LABEL_STYLE} htmlFor="firstName">First name</label>
                    <input type="text" id="firstName" name="firstName" style={INPUT_STYLE}
                      value={signupFormValue.firstName} onChange={handleSignupChange} required />
                    {signupFormErrors.firstName && <p style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>{signupFormErrors.firstName}</p>}
                  </div>
                  <div>
                    <label style={LABEL_STYLE} htmlFor="lastName">Last name</label>
                    <input type="text" id="lastName" name="lastName" style={INPUT_STYLE}
                      value={signupFormValue.lastName} onChange={handleSignupChange} required />
                    {signupFormErrors.lastName && <p style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>{signupFormErrors.lastName}</p>}
                  </div>
                </div>

                {/* Department */}
                <div style={{ marginBottom: 14 }}>
                  <label style={LABEL_STYLE} htmlFor="department">Department</label>
                  <select
                    id="department" name="department"
                    style={{ ...INPUT_STYLE, color: signupFormValue.department ? "#0f172a" : "#94a3b8" }}
                    value={signupFormValue.department} onChange={handleSignupChange}
                  >
                    <option value="">Select department</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  {signupFormErrors.department && <p style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>{signupFormErrors.department}</p>}
                </div>

                {/* Email */}
                <div style={{ marginBottom: 14 }}>
                  <label style={LABEL_STYLE} htmlFor="signupEmail">Email address</label>
                  <input type="email" id="signupEmail" name="signupEmail"
                    placeholder="name@email.com" style={INPUT_STYLE}
                    value={signupFormValue.signupEmail} onChange={handleSignupChange} required />
                  {signupFormErrors.signupEmail && <p style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>{signupFormErrors.signupEmail}</p>}
                </div>

                {/* Password / Confirm */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
                  <div>
                    <label style={LABEL_STYLE} htmlFor="signupPassword">Password</label>
                    <div style={{ position: "relative" }}>
                      <input type={showSignupPassword ? "text" : "password"} id="signupPassword" name="signupPassword"
                        style={{ ...INPUT_STYLE, paddingRight: 40 }}
                        value={signupFormValue.signupPassword} onChange={handleSignupChange} required />
                      <button
                        type="button"
                        onClick={() => setShowSignupPassword(v => !v)}
                        style={PASSWORD_TOGGLE_STYLE}
                        aria-label={showSignupPassword ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        {showSignupPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
                      </button>
                    </div>
                    {signupFormErrors.signupPassword && <p style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>{signupFormErrors.signupPassword}</p>}
                  </div>
                  <div>
                    <label style={LABEL_STYLE} htmlFor="confirmPassword">Confirm</label>
                    <div style={{ position: "relative" }}>
                      <input type={showConfirmPassword ? "text" : "password"} id="confirmPassword" name="confirmPassword"
                        style={{ ...INPUT_STYLE, paddingRight: 40 }}
                        value={signupFormValue.confirmPassword} onChange={handleSignupChange} required />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(v => !v)}
                        style={PASSWORD_TOGGLE_STYLE}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
                      </button>
                    </div>
                    {signupFormErrors.confirmPassword && <p style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>{signupFormErrors.confirmPassword}</p>}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSignupLoading}
                  style={{
                    width: "100%", padding: 13, background: t.pri, color: "#fff",
                    border: "none", borderRadius: 9, fontSize: 15, fontWeight: 700,
                    cursor: isSignupLoading ? "not-allowed" : "pointer",
                    fontFamily: "inherit", opacity: isSignupLoading ? 0.8 : 1, transition: "opacity 0.15s",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {isSignupLoading ? <Loader size="sm" /> : "Create Account"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
